// T_pqbl_torus — composite template content model, Rules, and Checks.
//
// Reference spec: `docs/model/bcs/template/pqbl-torus.md`.
//
// Shape: Course → Modules → Pages → MCQs → Choices. A Page maps to
// one Torus page and its MCQs become the activities on that page.
//
// Rule split (per Structure-vs-Content lens):
//   - Structural Rules — falls out of zod's schema validation
//     (missing fields, wrong types, empty strings, wrong tuple length).
//   - Semantic Rules — enforced by `checkSemantics` after zod parses:
//     duplicate SkillIds, unresolved SkillRefs, uncovered Skills,
//     wrong count of correct choices per MCQ.
//
// Both kinds surface as the same `RuleViolation` sum. The caller
// sees one flat list; `parse` collects the whole list before
// returning.
//
// Pure module. Zod is a pure runtime-type layer — no I/O, no clocks.
// LLM-eval Checks (distractor plausibility, stem clarity, feedback
// quality) still live in the shell that has an agent handle.
export { CONTENT_SHAPE, catalogueEntry, decodeDraft, describe, evaluateDraft, parse, runChecks, };
import { exhaustSwitchCase, Result } from "@bilda/utils";
import { z } from "zod";
import * as Diagnostic from "../diagnostic.js";
const brand = () => z
    .string()
    .min(1)
    .transform((s) => s);
const nonEmpty = brand();
const skillId = brand();
const loId = brand();
const moduleId = brand();
const pageId = brand();
const mcqId = brand();
// === Zod schemas ===
const SkillSchema = z.object({
    id: skillId,
    label: nonEmpty,
});
const LearningObjectiveSchema = z.object({
    id: loId,
    label: nonEmpty,
    skills: z.array(SkillSchema).min(1),
});
const ObjectivesSchema = z.object({
    objectives: z.array(LearningObjectiveSchema).min(1),
});
const ChoiceSchema = z.object({
    text: nonEmpty,
    correct: z.boolean(),
    feedback: nonEmpty,
});
const McqSchema = z.object({
    id: mcqId,
    testedSkills: z.array(skillId).min(1),
    stem: nonEmpty,
    choices: z.tuple([ChoiceSchema, ChoiceSchema, ChoiceSchema, ChoiceSchema]),
});
const PageSchema = z.object({
    id: pageId,
    title: nonEmpty,
    mcqs: z.array(McqSchema).min(1),
});
const ModuleSchema = z.object({
    id: moduleId,
    title: nonEmpty,
    pages: z.array(PageSchema).min(1),
});
const CourseSchema = z.object({
    title: nonEmpty,
    modules: z.array(ModuleSchema).min(1),
});
const ContentSchema = z.object({
    objectives: ObjectivesSchema,
    course: CourseSchema,
});
// === Rule projection ===
//
// The RuleViolation sum is PqblTorus's domain vocabulary; `describe`
// projects each variant into the shared `Diagnostic` carrier the UI
// reads. Exhaustive — a new RuleViolation variant breaks compilation
// here until it is given a code + params + fallback message. Every
// projection is an error severity (Rules are hard, by definition),
// and forwards the `path` anchor for navigation.
const describe = (v) => {
    switch (v.type) {
        case "structural":
            return Diagnostic.mkError({ code: "pqbl.structural", params: { zodCode: v.zodCode } }, v.reason, v.path);
        case "duplicate_skill_id":
            return Diagnostic.mkError({
                code: "pqbl.duplicate_skill_id",
                params: { skillId: v.skillId },
            }, `skill id "${v.skillId}" appears more than once`, v.path);
        case "skill_ref_unresolved":
            return Diagnostic.mkError({
                code: "pqbl.skill_ref_unresolved",
                params: { mcqId: v.mcqId, ref: v.ref },
            }, `references unknown skill "${v.ref}"`, v.path);
        case "skill_uncovered":
            return Diagnostic.mkError({
                code: "pqbl.skill_uncovered",
                params: { skillId: v.skillId },
            }, `skill "${v.skillId}" is not tested by any MCQ`, v.path);
        default:
            return exhaustSwitchCase(v);
    }
};
// === Parse ===
//
// Two-stage: zod covers structural Rules; `checkSemantics` covers
// cross-cutting invariants. Both stages accumulate into the same
// RuleViolation list.
const parse = (input) => {
    const parsed = ContentSchema.safeParse(input);
    if (!parsed.success) {
        return Result.err(parsed.error.issues.map(zodIssueToViolation));
    }
    const semantic = checkSemantics(parsed.data);
    if (semantic.length > 0)
        return Result.err(semantic);
    return Result.ok(parsed.data);
};
// The tolerant companion to strict `parse`: runs ONLY the structural zod
// schema, skipping the semantic gate. Returns the decoded content on structural
// success — a structurally-valid-but-semantically-incomplete draft (e.g. an
// uncovered skill) decodes here where `parse` would reject it — and `undefined`
// on structural failure. `ContentSchema` yields `PqblTorusContent`, which is
// assignable to the looser `DraftContent` the verifier reads (the same widening
// `parse` relies on when it hands `parsed.data` to `checkSemantics`). This feeds
// the live build-score signal, which wants a score for any parseable draft, not
// only a fully-valid one.
const decodeDraft = (input) => {
    const parsed = ContentSchema.safeParse(input);
    return parsed.success ? parsed.data : undefined;
};
const zodIssueToViolation = (issue) => ({
    type: "structural",
    // zod 4 types path keys as PropertyKey; this schema has no symbol keys.
    path: issue.path.filter((k) => typeof k !== "symbol"),
    zodCode: issue.code,
    reason: issue.message,
});
const checkSemantics = (content) => {
    const out = [];
    // First-occurrence path per skill id + duplicate detection.
    const skillPath = new Map();
    content.objectives.objectives.forEach((lo, loi) => {
        lo.skills.forEach((s, si) => {
            const path = ["objectives", "objectives", loi, "skills", si];
            if (skillPath.has(s.id)) {
                out.push({ type: "duplicate_skill_id", skillId: s.id, path });
            }
            else {
                skillPath.set(s.id, path);
            }
        });
    });
    // Per-MCQ correct-count (exactly 1) + referential integrity, each
    // anchored to the MCQ's path. Structural tuple-length already
    // guarantees 4 choices; count is a semantic invariant.
    const referenced = new Set();
    content.course.modules.forEach((m, mi) => {
        m.pages.forEach((p, pi) => {
            p.mcqs.forEach((mcq, qi) => {
                const path = ["course", "modules", mi, "pages", pi, "mcqs", qi];
                // The exactly-one-correct invariant moved OUT of pqbl-torus:
                // the material AST's `correct: ChoiceId` makes 0/2-correct
                // unrepresentable, so the old `mcq_wrong_correct_count` Rule
                // dissolved (D1 / NF-1). Only referential integrity remains.
                for (const ref of mcq.testedSkills) {
                    referenced.add(ref);
                    if (!skillPath.has(ref)) {
                        out.push({
                            type: "skill_ref_unresolved",
                            mcqId: mcq.id,
                            ref,
                            path,
                        });
                    }
                }
            });
        });
    });
    // Uncovered skills — defined but never tested by any MCQ.
    for (const [skillId, path] of skillPath) {
        if (!referenced.has(skillId)) {
            out.push({ type: "skill_uncovered", skillId, path });
        }
    }
    return out;
};
// === Traversal helpers ===
const allMcqs = function* (content) {
    for (const m of content.course.modules) {
        for (const p of m.pages) {
            for (const mcq of p.mcqs)
                yield mcq;
        }
    }
};
// === Checks — advisory, post-parse ===
//
// MVP: deterministic checks only. LLM-eval Checks named in the spec
// (distractor plausibility, stem clarity, label atomicity, feedback
// quality) live in the shell that has an agent handle. When a
// shell-side check catalogue lands it appends to the same
// `Diagnostic` shape.
const CHECK_MODULE_COUNT_MIN = 3;
const CHECK_MODULE_COUNT_MAX = 8;
const CHECK_PAGE_LENGTH_MIN = 5;
const CHECK_PAGE_LENGTH_MAX = 15;
const CHECK_COVERAGE_TARGET = 3;
const CHECK_CHOICE_LENGTH_TOLERANCE = 0.5; // ±50% of median
const runChecks = (content) => [
    ...checkModuleCount(content),
    ...checkPageLength(content),
    ...checkSkillCoverageDepth(content),
    ...checkChoiceStructuralParity(content),
];
const checkModuleCount = (content) => {
    const n = content.course.modules.length;
    if (n >= CHECK_MODULE_COUNT_MIN && n <= CHECK_MODULE_COUNT_MAX)
        return [];
    return [
        Diagnostic.mkCheck("warning", {
            code: "pqbl.module_count",
            params: {
                count: n,
                min: CHECK_MODULE_COUNT_MIN,
                max: CHECK_MODULE_COUNT_MAX,
            },
        }, `course has ${n} modules; ${CHECK_MODULE_COUNT_MIN}-${CHECK_MODULE_COUNT_MAX} is typical`, ["course", "modules"]),
    ];
};
const checkPageLength = (content) => content.course.modules.flatMap((m, mi) => m.pages.flatMap((p, pi) => {
    const n = p.mcqs.length;
    if (n >= CHECK_PAGE_LENGTH_MIN && n <= CHECK_PAGE_LENGTH_MAX)
        return [];
    return [
        Diagnostic.mkCheck("warning", {
            code: "pqbl.page_length",
            params: {
                title: p.title,
                count: n,
                min: CHECK_PAGE_LENGTH_MIN,
                max: CHECK_PAGE_LENGTH_MAX,
            },
        }, `page "${p.title}" has ${n} MCQs; ${CHECK_PAGE_LENGTH_MIN}-${CHECK_PAGE_LENGTH_MAX} is typical`, ["course", "modules", mi, "pages", pi, "mcqs"]),
    ];
}));
const checkSkillCoverageDepth = (content) => {
    const counts = new Map();
    for (const mcq of allMcqs(content)) {
        for (const ref of mcq.testedSkills) {
            counts.set(ref, (counts.get(ref) ?? 0) + 1);
        }
    }
    return content.objectives.objectives.flatMap((lo, loi) => lo.skills.flatMap((skill, si) => {
        const n = counts.get(skill.id) ?? 0;
        if (n >= CHECK_COVERAGE_TARGET)
            return [];
        return [
            Diagnostic.mkCheck("info", {
                code: "pqbl.skill_coverage_depth",
                params: {
                    label: skill.label,
                    count: n,
                    target: CHECK_COVERAGE_TARGET,
                },
            }, `skill "${skill.label}" is tested by ${n} MCQ(s); ${CHECK_COVERAGE_TARGET}+ is recommended`, ["objectives", "objectives", loi, "skills", si]),
        ];
    }));
};
const checkChoiceStructuralParity = (content) => content.course.modules.flatMap((m, mi) => m.pages.flatMap((p, pi) => p.mcqs.flatMap((mcq, qi) => {
    const lengths = mcq.choices.map((c) => c.text.length);
    const sorted = [...lengths].sort((a, b) => a - b);
    const median = sorted[2] ?? 0;
    const lo = median * (1 - CHECK_CHOICE_LENGTH_TOLERANCE);
    const hi = median * (1 + CHECK_CHOICE_LENGTH_TOLERANCE);
    const outliers = lengths.filter((l) => l < lo || l > hi);
    if (outliers.length === 0)
        return [];
    return [
        Diagnostic.mkCheck("info", {
            code: "pqbl.choice_structural_parity",
            params: { stem: mcq.stem, lengths, median },
        }, `MCQ "${mcq.stem.slice(0, 40)}…" has choices with lengths ${lengths.join("/")} (median ${median}); consider levelling`, ["course", "modules", mi, "pages", pi, "mcqs", qi]),
    ];
})));
// === Draft evaluation ===
//
// The editor's single diagnostics entry point. Runs the semantic
// Rules (as errors) and advisory Checks (as warnings) over in-progress
// content, tolerating structural incompleteness — no parse gate, so a
// half-authored draft still gets live feedback. Returns the merged
// `Report` the editor renders; every diagnostic carries a `path` the
// editor resolves to the offending node.
// During drafting, a genuine malformation (an MCQ with the wrong
// correct-count, a reference to a skill that doesn't exist) is an
// error. A "not-done-yet" state — a declared skill not yet tested, a
// duplicate id — is a warning, not a blocker: the author is mid-flight
// and shouldn't see red for work they haven't reached. (The strict
// `parse` gate still treats all of these as hard Rules.)
const DRAFT_ERROR_VARIANTS = new Set([
    "structural",
    "skill_ref_unresolved",
]);
const evaluateDraft = (content) => {
    const errors = [];
    const warnings = [...runChecks(content)];
    for (const v of checkSemantics(content)) {
        const d = describe(v);
        if (DRAFT_ERROR_VARIANTS.has(v.type)) {
            errors.push(d);
        }
        else {
            warnings.push(Diagnostic.downgrade(d, "warning"));
        }
    }
    return { errors, warnings };
};
// The content shape + its invariants, agent-register. One string for every
// prompt that asks a model to write T_pqbl_torus content (the chat agent's
// catalogue guidance, the generation job's part prompt), so they never drift.
const CONTENT_SHAPE = "`{objectives: {objectives: [{id, label, skills: [{id, label}, ...]}, ...]}, course: {title, modules: [{id, title, pages: [{id, title, mcqs: [{id, testedSkills: [SkillId, ...], stem, choices: [{text, correct, feedback}, {text, correct, feedback}, {text, correct, feedback}, {text, correct, feedback}]}, ...]}, ...]}, ...]}}`. Each Page maps to one Torus page and its `mcqs` become the activities on that page. Every id is a non-empty string; each MCQ has exactly 4 choices with exactly 1 correct; every SkillId referenced from an MCQ's testedSkills must appear in the objectives graph; SkillIds must be unique across the whole objectives graph.";
// === Catalogue entry ===
//
// Agent-facing capability advert per IDD c6b98441. Description is
// human-register (one line); guidance is agent-register (paragraph
// spelling out when to reach for the template, what inputs it needs,
// and what the loop looks like).
const catalogueEntry = {
    id: "T_pqbl_torus",
    name: "Torus course",
    description: "Torus-compatible pQBL: modules of pages, each page a set of MCQs on a shared learning objective, packageable to an oli-torus zip.",
    guidance: `Reach for this when the Author wants LMS-ready quiz content (Torus). Pairs with target format **F_torus** (packaging refuses other formats). Requires at least one source (PDF or text) as ground truth. Content shape (pass as the \`content\` string to create_material_from_source, JSON-serialised): ${CONTENT_SHAPE}`,
};
//# sourceMappingURL=pqblTorus.js.map